/**
 * Há quanto tempo uma coisa aconteceu — a régua do comentário e da notificação.
 *
 * <p>Estava escrita duas vezes, com os mesmos quatro limiares (1 min, 1 hora, 1 dia, 1
 * semana) copiados em `Conversa` e em `Notificacoes`. Mexer num limiar num
 * lugar e não no outro faria a mesma linha do tempo contar duas histórias — é a lição do
 * `progressoGeral` e do `iniciais`, e a razão de esta função morar em `comum/`.
 *
 * <p><b>Uma das cópias escrevia "há 1 dias"</b>, e escrevia isso o dia inteiro: qualquer
 * comentário entre 24 e 48 horas de idade caía no ramo dos dias com o contador em 1. Não
 * quebrava nada, aparecia só na tela e num intervalo que passa sozinho — por isso durou.
 *
 * <p>As duas formas continuam diferentes de propósito. A longa acompanha um texto e
 * precisa soar como português ("há 3 dias"); a curta mora numa pastilha ao lado de um
 * ícone, onde cabe "3d" e mais nada. O que elas não podem é discordar de <b>quando</b>
 * trocar de unidade, e é isso que passou a morar num lugar só.
 */

const HORA = 60;
const DIA = 60 * 24;
const SEMANA = 60 * 24 * 7;

/**
 * Minutos desde o instante, ou nulo quando a data não dá para ler.
 *
 * <p>Nulo não é preciosismo: `criadoEm` já voltou nulo da API cinco vezes neste projeto
 * — é a armadilha do `insertable = false` documentada no CLAUDE.md. `new Date(null)` não
 * estoura, produz uma data inválida, e toda comparação com ela é falsa; sem esta guarda,
 * o texto caía no último ramo e a tela escrevia **"Invalid Date"** para o usuário.
 */
function minutosDesde(iso: string | null | undefined, agora: Date): number | null {
  if (!iso) return null;
  const quando = new Date(iso).getTime();
  if (Number.isNaN(quando)) return null;
  return (agora.getTime() - quando) / 60000;
}

/**
 * "agora", "há 5 min", "há 3h", "há 2 dias", ou a data cheia.
 *
 * <p>Passada a semana o relativo perde a graça — "há 43 dias" não diz quando —, e a data
 * cheia volta, com ano: uma conversa de projeto atravessa a virada do ano.
 */
export function tempoRelativo(iso: string | null | undefined, agora = new Date()): string {
  const min = minutosDesde(iso, agora);
  if (min === null) return '';
  if (min < 1) return 'agora';
  if (min < HORA) return `há ${Math.floor(min)} min`;
  if (min < DIA) return `há ${Math.floor(min / HORA)}h`;
  if (min < SEMANA) {
    const dias = Math.floor(min / DIA);
    // O plural que faltava. Um dia é "há 1 dia", e não "há 1 dias".
    return `há ${dias} dia${dias === 1 ? '' : 's'}`;
  }
  return new Date(iso as string).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * A mesma régua sem as palavras: "agora", "5 min", "3h", "2d", "04/08".
 *
 * <p>Sem ano de propósito — a pastilha fica dentro do sino de notificações, onde a
 * largura é o recurso escasso e nada com mais de uma semana costuma continuar ali.
 */
export function tempoRelativoCurto(iso: string | null | undefined, agora = new Date()): string {
  const min = minutosDesde(iso, agora);
  if (min === null) return '';
  if (min < 1) return 'agora';
  if (min < HORA) return `${Math.floor(min)} min`;
  if (min < DIA) return `${Math.floor(min / HORA)}h`;
  if (min < SEMANA) return `${Math.floor(min / DIA)}d`;
  return new Date(iso as string).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
}
